import { ref, computed } from 'vue'
import { moveApi } from '../services/moveApi'
import { mapTaskFromApi } from '../utils/moveData'

const boards = ref([])
const tasks = ref([])
const users = ref([])
const loaded = ref(false)
const loading = ref(false)
let loadPromise = null

function matchesQuery (text, query) {
  return String(text || '').toLowerCase().includes(query)
}

export function useGlobalSearch () {
  const loadIndex = async (isAdmin = false) => {
    if (loaded.value && !loading.value) return
    if (loadPromise) return loadPromise

    loading.value = true
    loadPromise = (async () => {
      try {
        const requests = [
          moveApi.listBoards(),
          moveApi.myTasks()
        ]
        if (isAdmin) requests.push(moveApi.listUsers())

        const results = await Promise.all(requests)
        boards.value = results[0].data.data || []
        tasks.value = (results[1].data.data || []).map(mapTaskFromApi)

        if (isAdmin && results[2]) {
          users.value = (results[2].data.data || []).map((user) => ({
            id: user.id,
            email: user.email,
            name: user.name,
            surname: user.surname,
            role: user.role
          }))
        } else {
          users.value = []
        }

        loaded.value = true
      } catch (error) {
        console.error('Global search index failed:', error)
      } finally {
        loading.value = false
        loadPromise = null
      }
    })()

    return loadPromise
  }

  const invalidateIndex = () => {
    loaded.value = false
  }

  const search = (query, { isAdmin = false, limit = 5 } = {}) => {
    const q = query.trim().toLowerCase()
    if (!q) {
      return { boards: [], tasks: [], users: [], total: 0 }
    }

    const boardResults = boards.value
      .filter((board) => matchesQuery(board.name, q))
      .slice(0, limit)
      .map((board) => ({
        type: 'board',
        id: board.id,
        title: board.name,
        subtitle: board.is_owner ? 'Your board' : 'Shared board',
        icon: 'view_kanban',
        to: `/board/${board.id}`
      }))

    const taskResults = tasks.value
      .filter((task) => !task.archived)
      .filter((task) =>
        matchesQuery(task.title, q) ||
        matchesQuery(task.description, q) ||
        matchesQuery(task.boardName, q) ||
        (task.tags || []).some((tag) => matchesQuery(tag, q))
      )
      .slice(0, limit)
      .map((task) => ({
        type: 'task',
        id: task.id,
        title: task.title,
        subtitle: task.boardName || 'Task',
        icon: 'task_alt',
        to: `/board/${task.boardId}?task=${task.id}`
      }))

    const userResults = isAdmin
      ? users.value
        .filter((user) =>
          matchesQuery(user.email, q) ||
          matchesQuery(user.name, q) ||
          matchesQuery(user.surname, q) ||
          matchesQuery(`${user.name || ''} ${user.surname || ''}`, q)
        )
        .slice(0, limit)
        .map((user) => {
          const name = [user.name, user.surname].filter(Boolean).join(' ') || user.email.split('@')[0]
          return {
            type: 'user',
            id: user.id,
            title: name,
            subtitle: user.email,
            icon: 'person',
            to: `/users?q=${encodeURIComponent(q)}`
          }
        })
      : []

    return {
      boards: boardResults,
      tasks: taskResults,
      users: userResults,
      total: boardResults.length + taskResults.length + userResults.length
    }
  }

  const flatResults = (query, options) => {
    const results = search(query, options)
    return [...results.boards, ...results.tasks, ...results.users]
  }

  const indexStats = computed(() => ({
    boards: boards.value.length,
    tasks: tasks.value.length,
    users: users.value.length
  }))

  return {
    boards,
    tasks,
    users,
    loaded,
    loading,
    indexStats,
    loadIndex,
    invalidateIndex,
    search,
    flatResults
  }
}
